import {
  PROGRESS_VISIBILITIES,
  RESPONDER_PHASES,
  type ProgressVisibility,
} from './shared-responder-v2-core-session.consts';
import type {
  ResponderCapabilities,
  ResponderSessionState,
  ResponderStep,
} from './shared-responder-v2-core-session.types';

export function isFreeJumpOpen(
  capabilities: Pick<
    ResponderCapabilities,
    'canJumpForward' | 'hasBranching' | 'hasUrlOrTask'
  >
): boolean {
  return (
    capabilities.canJumpForward &&
    !capabilities.hasBranching &&
    !capabilities.hasUrlOrTask
  );
}

function findBranchingBarrierIndex(
  steps: readonly ResponderStep[],
  maxReachedStepIndex: number
): number {
  return steps.findIndex(
    (step, index) => index > maxReachedStepIndex && step.hasBranching
  );
}

/**
 * The furthest step index the responder may navigate to right now. The
 * backend's `nextQuestionId` can only ever narrow this, never widen it, since
 * it is the sole authority on branching outcomes. Once the backend has
 * declared the survey complete, no step past `maxReachedStepIndex` was ever
 * legitimately offered — trusting that completion (same rule `advance` uses)
 * closes the jump gate outright, the same as a branching project.
 */
export function computeReachableUpperBound(args: {
  steps: readonly ResponderStep[];
  maxReachedStepIndex: number;
  canJumpForward: boolean;
  hasUrlOrTask: boolean;
  nextQuestionId: string | null;
  /** Closes the jump gate outright; see `ResponderCapabilities.hasBranching`. */
  hasBranching?: boolean;
  isComplete?: boolean;
}): number {
  const {
    steps,
    maxReachedStepIndex,
    canJumpForward,
    hasUrlOrTask,
    nextQuestionId,
    hasBranching = false,
    isComplete = false,
  } = args;

  if (
    !isFreeJumpOpen({ canJumpForward, hasBranching, hasUrlOrTask }) ||
    isComplete
  ) {
    return Math.max(maxReachedStepIndex, 0);
  }

  const barrierIndex = findBranchingBarrierIndex(steps, maxReachedStepIndex);
  let ceiling = barrierIndex === -1 ? steps.length - 1 : barrierIndex;

  if (nextQuestionId !== null) {
    const nextIndex = steps.findIndex(
      (step) => step.questionId === nextQuestionId
    );

    if (nextIndex !== -1) {
      ceiling = Math.min(ceiling, nextIndex);
    }
  }

  return Math.max(ceiling, maxReachedStepIndex, 0);
}

/**
 * The furthest step the progress list may **display**, distinct from the jump ceiling
 * `computeReachableUpperBound` computes. Truncated only at the first branching question, the
 * point past which order isn't known yet — `hasUrlOrTask`/`canJumpForward` don't affect this,
 * they only forbid clicking ahead, which `isStepReachable` still enforces per item. A backend-
 * declared-complete survey truncates here too: a step past `maxReachedStepIndex` was never
 * visited, so it was skipped (e.g. Contact Info suppressed by an `?email=` link), not upcoming.
 */
export function computeVisibleUpperBound(args: {
  steps: readonly ResponderStep[];
  maxReachedStepIndex: number;
  isComplete?: boolean;
}): number {
  const { steps, maxReachedStepIndex, isComplete = false } = args;

  if (isComplete) {
    return Math.max(maxReachedStepIndex, 0);
  }

  const barrierIndex = findBranchingBarrierIndex(steps, maxReachedStepIndex);
  const ceiling = barrierIndex === -1 ? steps.length - 1 : barrierIndex;

  return Math.max(ceiling, maxReachedStepIndex, 0);
}

/**
 * Before "Begin session" no question is reachable: `moveTo` sets `currentStepIndex`/
 * `maxReachedStepIndex` but never touches `phase`, so a jump on the Intro would move the index
 * while the view stayed on Intro. Gating here, not in the rail's click handler, makes the click
 * a no-op for every caller: `resolveStepSelection`, `jumpTo`, and the progress-item's flag.
 */
export function isStepReachable(
  state: ResponderSessionState,
  index: number
): boolean {
  if (index < 0 || index >= state.steps.length) {
    return false;
  }

  if (state.phase === RESPONDER_PHASES.Intro) {
    return false;
  }

  if (index <= state.maxReachedStepIndex) {
    return true;
  }

  const upperBound = computeReachableUpperBound({
    steps: state.steps,
    maxReachedStepIndex: state.maxReachedStepIndex,
    canJumpForward: state.capabilities.canJumpForward,
    hasBranching: state.capabilities.hasBranching,
    hasUrlOrTask: state.capabilities.hasUrlOrTask,
    nextQuestionId: state.nextQuestionId,
    isComplete: state.isComplete,
  });

  return index <= upperBound;
}

export function hasBranchingBarrier(args: {
  steps: readonly ResponderStep[];
  maxReachedStepIndex: number;
}): boolean {
  return findBranchingBarrierIndex(args.steps, args.maxReachedStepIndex) !== -1;
}

export function resolveProgressVisibility(args: {
  hasUrlOrTask: boolean;
  canJumpForward: boolean;
  hasBranchingBarrier: boolean;
}): ProgressVisibility {
  if (args.hasUrlOrTask || !args.canJumpForward || args.hasBranchingBarrier) {
    return PROGRESS_VISIBILITIES.BackwardOnly;
  }

  return PROGRESS_VISIBILITIES.Full;
}
